import { AxiosError } from 'axios'

// Kept apart from client.ts, which imports the API client and the notification UI, so the
// policy can be loaded and tested outside Vite (tests/e2e/api/retry-policy.spec.ts).

const MAX_RETRIES = 3

// The two 4xx codes that describe a temporary condition rather than a verdict about the
// request itself, so repeating the identical request can legitimately succeed:
//   429 — rate limited; the caller is expected to back off and retry.
//   408 — the server gave up waiting for the request; it was never processed.
const RETRYABLE_CLIENT_STATUSES = new Set([408, 429])

// Every backend route is scope-protected, so a 401/403/404 is a verdict, not a blip —
// retrying it just quadruples the failed requests and holds `isFetching` true through the
// backoff, which reads as a hung spinner. Retry only what could plausibly succeed on a
// second attempt: 5xx, network errors, and the two transient 4xx codes above.
//
// React Query's default is 3 retries on everything, including permission denials.
export const retryOnlyTransient = (failureCount: number, error: unknown) => {
  const status = (error as AxiosError)?.response?.status

  const isClientVerdict =
    status !== undefined &&
    status >= 400 &&
    status < 500 &&
    !RETRYABLE_CLIENT_STATUSES.has(status)

  if (isClientVerdict) return false

  return failureCount < MAX_RETRIES
}
